<template>
    <div>
        <h2 class="font-semibold text-gray-800 text-lg text-left leading-snug mb-4">
            {{ $t('For Team Leaders') }}
        </h2>

        <p class="text-gray-700 text-sm text-left leading-snug py-1">
            {{ $t('You can view the properties and deals belonging to these Team Members.') }}
        </p>

        <div>
            <div v-for="share in shared_accounts" :key="share.id"
                class="flex w-full mb-6 px-6 py-5 text-sm md:text-base xl:text-md leading-none"
                :class="share.is_authorized ? 'border-2 border-green-warm-500 bg-white' : 'border border-gray-400 bg-gray-warm-75'"
            >
                <!-- Accepted invitation, active share -->
                <div v-if="share.is_authorized"
                    class="flex flex-col md:flex-row flex-1 md:items-end md:justify-between"
                >
                    <div class="pt-1 pb-1">
                        <div class="flex flex-wrap">
                            <div class="py-1 font-semibold mr-8">
                                <i class="fa fa-user mr-2 text-sm text-green-warm-600 mb-1 relative" style="bottom: 1px"></i> {{ share.user.name }}
                            </div>

                            <div class="py-1">
                                <i class="fa fa-envelope mr-2 text-sm text-green-warm-600"></i> {{ share.user.email }}
                            </div>
                        </div>
                    </div>

                    <div class="flex flex-wrap items-center justify-end">
                        <link-gray v-if="share.is_revokable" class="pt-2 md:pb-2"
                            @click="deleteRequest(share)"
                        >
                            <i class="fa fa-times mr-1"></i> {{ $t('Stop Sharing') }}
                        </link-gray>
                    </div>
                </div>

                <!-- Pending request to another user to share their account with me -->
                <div v-else-if="share.requested_by_me"
                    class="w-full"
                >
                    <div class="text-blue-500 uppercase text-xs tracking-wider font-semibold leading-normal mb-1">
                        {{ $t('Request sent to:') }}
                    </div>

                    <div class="flex flex-col md:flex-row flex-1 md:items-end md:justify-between">
                        <div class="py-2">
                            <i class="fa fa-envelope mr-2 text-sm text-gray-warm-600"></i> {{ share.requested_to_email }}
                        </div>

                        <div class="flex flex-wrap items-center justify-end pt-4 md:pt-0">
                            <link-gray class="ml-4 md:py-2"
                                @click="deleteRequest(share)"
                            >
                                <i class="fa fa-times mr-1"></i> {{ $t('Cancel Request') }}
                            </link-gray>
                        </div>
                    </div>
                </div>

                <!-- Incoming invitation from another user to share their account with me -->
                <div v-else-if="! share.requested_by_me"
                    class="w-full"
                >
                    <div class="text-blue-500 uppercase text-xs tracking-wider font-semibold leading-normal mb-1">
                        {{ $t('A user is inviting you to view their account:') }}
                    </div>

                    <div class="flex flex-col md:flex-row flex-1 md:items-end md:justify-between">
                        <div class="pt-1 pb-1 md:pb-2">
                            <div class="flex flex-wrap">
                                <div class="py-1 font-semibold mr-8">
                                    <i class="fa fa-user mr-2 text-sm text-gray-warm-600 mb-1 relative" style="bottom: 1px"></i> {{ share.user.name }}
                                </div>

                                <div class="py-1">
                                    <i class="fa fa-envelope mr-2 text-sm text-gray-warm-600"></i> {{ share.user.email }}
                                </div>
                            </div>
                        </div>

                        <div class="flex flex-wrap items-center justify-end pt-4 md:pt-0">
                            <link-cancel v-if="share.is_revokable" class="md:py-4"
                                @click="deleteRequest(share)"
                            >
                                {{ $t('Reject') }}
                            </link-cancel>

                            <button-blue-outline v-if="share.is_revokable"
                                class="ml-4"
                                @click="acceptRequest(share)"
                            >
                                <i class="fa fa-check -ml-1 mr-1 text-xs"></i>
                                {{ $t('Accept') }} <span class="hidden sm:inline">{{ $t('Invitation') }}</span>
                            </button-blue-outline>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="flex justify-end">
            <form v-if="adding_broker"
                @submit.prevent="submitRequest"
                class="flex flex-col items-end border border-gray-400 p-6 bg-white shadow"
            >
                <div class="flex items-center mb-6">
                    <input-label>
                        {{ $t('Team Member\'s Email Address') }}
                    </input-label>

                    <input-text
                        class="w-96 ml-6"
                        name="broker_email"
                        type="text"
                        :spellcheck="false"
                        v-model="broker_email"
                        ref="broker_email"
                    />
                </div>

                <div v-if="error" class="text-right text-red-600 text-sm font-semibold -mt-2 mb-4 leading-none">
                    {{ error }}
                </div>

                <div class="flex w-full items-end justify-between">
                    <link-cancel class="mr-6"
                        @click="cancelRequest"
                    >
                        {{ $t('Cancel') }}
                    </link-cancel>

                    <button-blue type="submit">
                        {{ $t('Submit request') }}
                    </button-blue>
                </div>
            </form>

            <button-blue v-else @click="requestAccess">
                {{ $t('Request access to a team member\'s account') }}
            </button-blue>
        </div>
    </div>
</template>

<script>
import ButtonBlue from '../../../../components/button-blue.vue';
import ButtonBlueOutline from '../../../../components/button-blue-outline.vue';
import LinkCancel from '../../../../components/link-cancel.vue';
import LinkGray from '../../../../components/link-gray.vue';

export default {
    components: {
        ButtonBlue,
        ButtonBlueOutline,
        LinkCancel,
        LinkGray,
    },

    props: {
        broker: {},
        shared_with_me: {},
    },

    data() {
        return {
            adding_broker: false,
            broker_email: null,
            shared_accounts: this.shared_with_me,
            error: null,
        }
    },

    methods: {
        requestAccess() {
            this.adding_broker = true;
            this.$nextTick(() => {
                this.$refs['broker_email'].focus();
            });
        },
        cancelRequest() {
            this.adding_broker = false;
            this.broker_email = null;
        },
        submitRequest() {
            axios.post('/broker/settings/shared-accounts/with-me', { broker_email: this.broker_email })
                .then(response => {
                    this.adding_broker = false;
                    this.broker_email = null;
                    this.shared_accounts = response.data.data;
                }).catch(error => {
                    if (error?.response?.data?.errors?.broker_email?.[0]) {
                        this.error = error?.response?.data?.errors?.broker_email?.[0];
                    }
                });
        },
        acceptRequest(share) {
            axios.patch('/broker/settings/shared-accounts/with-me/' + share.id, { authorized: true })
                .then(response => {
                    this.shared_accounts = response.data.data;
                }).catch(error => {
                    //
                });
        },
        deleteRequest(share) {
            axios.delete('/broker/settings/shared-accounts/with-me/' + share.id)
                .then(response => {
                    this.shared_accounts = response.data.data;
                }).catch(error => {
                    //
                });
        },
    },
}
</script>
